// =============================================================================
// Overlap slider — gedeelde drag/snap/inertia-slider.
//
// Oorspronkelijk gebouwd voor het `collegas`-block (overlappende, geroteerde
// "stack"-kaarten). Geëxtraheerd zodat andere blocks (o.a. `vacatures-slider`)
// dezelfde mechanica kunnen hergebruiken met een andere vormgeving.
//
// Markup-contract op het init-element ([data-overlap-slider-init]):
//   - data-scale   : min-scale van een doorgeschoven kaart (default 0.92).
//   - data-rotate  : max-rotatie (graden) van een doorgeschoven kaart (-6).
//   - data-overlap : 'false' = vlakke horizontale slider (geen stack/scale/
//                    rotate); alles anders = klassieke overlap-stack.
//
// Binnen het init-element:
//   [data-overlap-slider-collection]  drag-surface (krijgt .is--dragging)
//   [data-overlap-slider-list]        flex-track dat transformt
//   [data-overlap-slider-item]        de losse kaarten
//
// Prev/next-knoppen worden in de dichtstbijzijnde <section> gezocht:
//   [data-overlap-slider-prev] / [data-overlap-slider-next]
// =============================================================================

import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';

gsap.registerPlugin( Draggable, InertiaPlugin );

const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

export function setupOverlappingSlider( init ) {
	// Idempotent — voorkom dubbele init wanneer meerdere modules de bootstrap
	// aanroepen (collegas.js én vacatures-slider.js delen deze lib).
	if ( init.dataset.overlapSliderReady === '1' ) {
		return;
	}
	init.dataset.overlapSliderReady = '1';

	const minScale    = parseFloat( init.getAttribute( 'data-scale' ) ?? '0.92' );
	const maxRotation = parseFloat( init.getAttribute( 'data-rotate' ) ?? '-6' );
	// Vlakke slider: kaarten schuiven normaal mee, geen stack/scale/rotate.
	const overlap     = init.getAttribute( 'data-overlap' ) !== 'false';

	const collection = init.querySelector( '[data-overlap-slider-collection]' );
	const list       = init.querySelector( '[data-overlap-slider-list]' );
	const items      = Array.from( init.querySelectorAll( '[data-overlap-slider-item]' ) );

	// Prev/next buttons live in a sibling (zie collegas.php → `__footer`),
	// dus zoeken we vanaf de dichtstbijzijnde section/region-ancestor.
	const scope   = init.closest( 'section' ) ?? init.parentElement ?? document;
	const prevBtn = scope.querySelector( '[data-overlap-slider-prev]' );
	const nextBtn = scope.querySelector( '[data-overlap-slider-next]' );

	if ( ! collection || ! list || ! items.length ) {
		return;
	}

	let spacing      = 0;
	let maxDrag      = 0;
	let dragX        = 0;
	let currentIndex = 0;
	let draggable;

	const clamp = ( value ) => {
		if ( maxDrag <= 0 ) {
			return 0;
		}
		return Math.min( Math.max( value, 0 ), maxDrag );
	};

	function update() {
		gsap.set( list, { x: -dragX } );

		items.forEach( ( item, i ) => {
			if ( ! overlap ) {
				// Vlakke slider — kaarten blijven in de flow, alleen de list
				// transformt. Reset eventuele eerdere transforms.
				gsap.set( item, { x: 0, scale: 1, rotation: 0 } );
				return;
			}

			const threshold = i * spacing;
			const local     = Math.max( 0, dragX - threshold );
			const t         = spacing > 0 ? Math.min( local / spacing, 1 ) : 0;

			gsap.set( item, {
				x:               local,
				scale:           1 - ( 1 - minScale ) * t,
				rotation:        maxRotation * t,
				transformOrigin: '75% center',
			} );
		} );

		updateNavState();
	}

	function updateNavState() {
		if ( prevBtn ) {
			prevBtn.toggleAttribute( 'disabled', currentIndex <= 0 );
		}
		if ( nextBtn ) {
			nextBtn.toggleAttribute( 'disabled', currentIndex >= items.length - 1 );
		}
	}

	function recalc() {
		if ( ! items.length ) {
			return;
		}

		const style    = getComputedStyle( list );
		const gapValue = parseFloat( style.columnGap || style.gap ) || 0;

		const itemWidth = items[ 0 ].offsetWidth;
		spacing = itemWidth + gapValue;
		maxDrag = spacing * ( items.length - 1 );

		dragX = clamp( dragX );
		update();

		if ( draggable ) {
			draggable.applyBounds( { minX: -maxDrag, maxX: 0 } );
		}
	}

	function goToIndex( idx, animated = true ) {
		const next = Math.max( 0, Math.min( idx, items.length - 1 ) );
		currentIndex = next;

		const targetX = next * spacing;

		if ( ! animated || prefersReducedMotion ) {
			dragX = targetX;
			update();
			init.setAttribute( 'aria-label', `Slide ${ next + 1 } of ${ items.length }` );
			return;
		}

		gsap.to( { value: dragX }, {
			value:    targetX,
			duration: 0.45,
			ease:     'power3.out',
			onUpdate() {
				dragX = this.targets()[ 0 ].value;
				update();
			},
		} );

		init.setAttribute( 'aria-label', `Slide ${ next + 1 } of ${ items.length }` );
	}

	// Draggable + Inertia — snap naar dichtstbijzijnde slide-index na throw.
	draggable = Draggable.create( list, {
		type:        'x',
		bounds:      { minX: -maxDrag, maxX: 0 },
		inertia:     ! prefersReducedMotion,
		maxDuration: 1,
		dragResistance: 0.05,
		edgeResistance: 0.85,
		snap: ( raw ) => {
			const d   = clamp( -raw );
			const idx = spacing > 0 ? Math.round( d / spacing ) : 0;
			return -idx * spacing;
		},
		onPress() {
			collection.classList.add( 'is--dragging' );
		},
		onRelease() {
			collection.classList.remove( 'is--dragging' );
		},
		onDrag() {
			dragX = clamp( -this.x );
			currentIndex = spacing > 0 ? Math.round( dragX / spacing ) : 0;
			update();
		},
		onThrowUpdate() {
			dragX = clamp( -this.x );
			currentIndex = spacing > 0 ? Math.round( dragX / spacing ) : 0;
			update();
		},
	} )[ 0 ];

	// Resize — recalc dimensies en bounds.
	const ro = new ResizeObserver( () => recalc() );
	ro.observe( init );

	// Keyboard nav — alleen actief als de slider in viewport zichtbaar is.
	let inView = false;
	const io = new IntersectionObserver(
		( entries ) => { inView = entries[ 0 ].isIntersecting; },
		{ threshold: 0.25 },
	);
	io.observe( init );

	function onKey( e ) {
		if ( ! inView ) {
			return;
		}

		if ( e.key === 'ArrowLeft' ) {
			e.preventDefault();
			goToIndex( currentIndex - 1 );
		} else if ( e.key === 'ArrowRight' ) {
			e.preventDefault();
			goToIndex( currentIndex + 1 );
		}
	}
	window.addEventListener( 'keydown', onKey );

	// Prev/next buttons.
	if ( prevBtn ) {
		prevBtn.addEventListener( 'click', () => goToIndex( currentIndex - 1 ) );
	}
	if ( nextBtn ) {
		nextBtn.addEventListener( 'click', () => goToIndex( currentIndex + 1 ) );
	}

	// Initial layout — recalc na paint zodat fonts/images correct gemeten zijn.
	recalc();
	requestAnimationFrame( recalc );
}

export function initOverlappingSliders() {
	const inits = document.querySelectorAll( '[data-overlap-slider-init]' );
	inits.forEach( setupOverlappingSlider );
}

// Bootstrap — idempotent (zie guard in setupOverlappingSlider), dus meerdere
// importeurs die deze init aanroepen is veilig.
export function bootstrapOverlappingSliders() {
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', initOverlappingSliders );
	} else {
		initOverlappingSliders();
	}
}
